/**
 * Группа «Критерии» — результаты кастомных критериев на этом звонке (#862,
 * спека §7). Строка: имя, значение, цитата с переходом к моменту.
 *
 * Цвета намеренно нейтральные. Полярность критерия нам неизвестна: «сработал»
 * у одного означает нарушение, у другого — выполненный шаг, и покрасить их
 * общим правилом значило бы утверждать оценку, которой никто не давал.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecordCriterionResult } from '@aica/api-types/schemas';
import { AnalysisSection, Tag } from './analysis-utils';
import {
  buildCriteriaMomentIndex,
  momentOfEvidence,
  type CriteriaSegment,
} from './criteria-moment';

interface CriteriaSectionProps {
  criteria: RecordCriterionResult[];
  loading?: boolean;
  /**
   * Запрос не удался. Карточка говорит об этом, а не исчезает: исчезновение
   * читается как «критериев нет», и PDF ушёл бы без раздела молча (находка
   * Codex на #1121).
   */
  failed?: boolean;
  /** Сегменты разговора: по ним цитата находит свой момент. */
  diarization?: CriteriaSegment[];
  /** Проиграть отрезок записи с этого места. */
  onPlaySegment?: (startSeconds: number, durationSeconds?: number) => void;
}

export function CriteriaSection({
  criteria,
  loading,
  failed,
  diarization,
  onPlaySegment,
}: CriteriaSectionProps) {
  const { t } = useTranslation('records');
  // Нумерация реплик — один раз на набор сегментов: панель перерисовывается на
  // каждый кадр воспроизведения, а разбор сегментов дорог.
  const moments = useMemo(() => buildCriteriaMomentIndex(diarization), [diarization]);

  if (failed && criteria.length === 0) {
    return (
      <AnalysisSection
        title={t('records.analysisPanel.criteria.title', 'Criteria Results')}
      >
        <p className="text-xs text-danger">
          {t('records.analysisPanel.criteria.failed', 'Could not load criteria results')}
        </p>
      </AnalysisSection>
    );
  }

  // Строки важнее признака загрузки: пока идёт повторный прогон они уже есть, и
  // прятать их за «загрузкой» значило бы терять показанный результат.
  if (loading && criteria.length === 0) {
    return (
      <AnalysisSection
        title={t('records.analysisPanel.criteria.title', 'Criteria Results')}
      >
        <p className="text-xs text-fg-muted">
          {t('records.analysisPanel.criteria.loading', 'Loading…')}
        </p>
      </AnalysisSection>
    );
  }

  if (criteria.length === 0) return null;

  const stateLabel = (state: RecordCriterionResult['state']): string =>
    ({
      answered: '',
      not_found: t('records.analysisPanel.criteria.state.notFound', 'Not found in call'),
      not_applicable: t(
        'records.analysisPanel.criteria.state.notApplicable',
        'Not applicable'
      ),
      not_evaluated: t(
        'records.analysisPanel.criteria.state.notEvaluated',
        'Not evaluated'
      ),
    })[state];

  const valueOf = (item: RecordCriterionResult): string => {
    if (item.resultType === 'choice') return item.valueLabel ?? '';
    if (item.resultType === 'score')
      return item.valueScore === null ? '' : `${item.valueScore}`;
    return item.valueBool
      ? t('records.analysisPanel.criteria.yes', 'Yes')
      : t('records.analysisPanel.criteria.no', 'No');
  };

  return (
    <AnalysisSection
      title={t('records.analysisPanel.criteria.title', 'Criteria Results')}
      summary={
        <span className="font-mono text-2xs text-fg-subtle">{criteria.length}</span>
      }
    >
      <div className="flex flex-col gap-2">
        {criteria.map((item) => (
          <div
            key={item.criterionId}
            className="flex flex-col gap-1 rounded-md bg-bg-surface p-2.5 text-xs"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex-1 font-medium text-fg">{item.name}</span>
              {item.state === 'answered' ? (
                <>
                  <Tag variant="neutral" className="text-micro py-0">
                    {valueOf(item)}
                  </Tag>
                  {item.triggered === true && (
                    <Tag variant="neutral" className="text-micro py-0">
                      {t('records.analysisPanel.criteria.triggered', 'Triggered')}
                    </Tag>
                  )}
                </>
              ) : (
                <span className="text-fg-subtle">{stateLabel(item.state)}</span>
              )}
            </div>
            {item.note && <p className="text-fg-secondary">{item.note}</p>}
            {item.evidence.map((evidence, idx) => {
              const seconds = momentOfEvidence(evidence, moments);
              const jumpable = seconds !== null && !!onPlaySegment;
              return (
                <div key={idx} className="flex items-start gap-2 text-fg-muted">
                  {jumpable ? (
                    <button
                      type="button"
                      onClick={() => onPlaySegment?.(seconds, 20)}
                      className="text-left italic text-primary underline underline-offset-2 hover:text-primary-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    >
                      «{evidence.quote}»
                    </button>
                  ) : (
                    <span className="italic">«{evidence.quote}»</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </AnalysisSection>
  );
}
